import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styles from './PurchaseSuccess.module.scss';
import { Routes } from '../../../types/constants';
import { completeVideoPurchase } from '../../../services/purchase.service';
import { usePurchaseStatus } from '../../../providers/purchase-status.provider';
import { useUserState } from '../../../providers/user-state.provider.ts';
import { trackMarketingConversion } from '../../../services/marketing.service';
import { createAnalyticsClientEventId } from '../../../services/analytics.service';
import { usePageActivationRefresh } from '../../../hooks/usePageActivationRefresh';

const PURCHASE_COMPLETION_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 16000];

const delay = (milliseconds: number) => new Promise((resolve) => setTimeout(resolve, milliseconds));

const PurchaseSuccess = () => {
    const [searchParams] = useSearchParams();
    const videoParam = searchParams.get('v') ?? '';
    const sessionId = searchParams.get('session_id') ?? '';
    const { getUserInfo, isUserStateLoading } = useUserState();
    const { refreshEntitlements } = usePurchaseStatus();
    const renderedUser = getUserInfo();
    const authToken = renderedUser?.token ?? '';
    const accountKey = renderedUser?.userId ?? authToken;
    const completionKey = `${accountKey}\u0000${sessionId}\u0000${videoParam}`;
    const [completionMessage, setCompletionMessage] = useState<string | null>(
        sessionId ? null : 'Purchase session is missing. Please contact support if this payment completed.'
    );
    const [isVerified, setIsVerified] = useState(false);
    const [isPending, setIsPending] = useState(!!sessionId);
    const [completionStateKey, setCompletionStateKey] = useState(completionKey);
    const isMountedRef = useRef(true);
    const isVerifyingRef = useRef(false);
    const isVerifiedRef = useRef(false);
    const purchaseEventIdRef = useRef(createAnalyticsClientEventId());
    const hasTrackedConversionRef = useRef(false);
    const authTokenRef = useRef(authToken);
    const completionKeyRef = useRef(completionKey);
    // Invalidate an in-flight verification before it can affect a different account or Checkout Session.
    if (authTokenRef.current !== authToken) {
        authTokenRef.current = authToken;
        isVerifyingRef.current = false;
    }
    if (completionKeyRef.current !== completionKey) {
        completionKeyRef.current = completionKey;
        isVerifyingRef.current = false;
        isVerifiedRef.current = false;
        purchaseEventIdRef.current = createAnalyticsClientEventId();
        hasTrackedConversionRef.current = false;
    }

    const isRunCurrent = useCallback((runToken: string, runCompletionKey: string) => (
        isMountedRef.current
        && authTokenRef.current === runToken
        && completionKeyRef.current === runCompletionKey
    ), []);
    const watchUrl = useMemo(() => {
        if (!videoParam) {
            return null;
        }
        return `${Routes.WATCH}?v=${encodeURIComponent(videoParam)}`;
    }, [videoParam]);

    const verifyPurchase = useCallback(async () => {
        if (isUserStateLoading || !sessionId || isVerifyingRef.current || isVerifiedRef.current) {
            return;
        }

        const token = authToken;
        if (getUserInfo()?.token !== token) {
            return;
        }
        const runCompletionKey = completionKey;
        if (!token) {
            if (isMountedRef.current) {
                setCompletionStateKey(runCompletionKey);
                setIsVerified(false);
                setIsPending(false);
                setCompletionMessage('We could not verify this purchase yet. Please refresh or contact support.');
            }
            return;
        }

        isVerifyingRef.current = true;
        if (isRunCurrent(token, runCompletionKey)) {
            setCompletionStateKey(runCompletionKey);
            setIsPending(true);
            setCompletionMessage(null);
        }

        try {
            let nextCompletionMessage = 'Payment has not completed yet.';
            let lastError: unknown = null;

            for (let attempt = 0; attempt <= PURCHASE_COMPLETION_RETRY_DELAYS_MS.length; attempt += 1) {
                try {
                    const result = await completeVideoPurchase(sessionId, token, purchaseEventIdRef.current);
                    if (!isRunCurrent(token, runCompletionKey)) {
                        return;
                    }

                    if (result.Completed) {
                        await refreshEntitlements();
                        if (!isRunCurrent(token, runCompletionKey)) {
                            return;
                        }

                        isVerifiedRef.current = true;
                        if (!hasTrackedConversionRef.current) {
                            hasTrackedConversionRef.current = true;
                            trackMarketingConversion({
                                kind: 'purchase',
                                eventId: purchaseEventIdRef.current,
                                orderId: sessionId,
                                metadata: {
                                    source: 'purchase_success',
                                    video: videoParam
                                }
                            });
                        }

                        if (isRunCurrent(token, runCompletionKey)) {
                            setCompletionStateKey(runCompletionKey);
                            setIsVerified(true);
                            setCompletionMessage(null);
                        }
                        return;
                    }

                    nextCompletionMessage = result.Message ?? 'Payment has not completed yet.';
                } catch (error) {
                    lastError = error;
                    nextCompletionMessage = 'We could not verify this purchase yet. Please refresh or contact support.';
                }

                if (attempt < PURCHASE_COMPLETION_RETRY_DELAYS_MS.length) {
                    await delay(PURCHASE_COMPLETION_RETRY_DELAYS_MS[attempt]);
                    if (!isRunCurrent(token, runCompletionKey)) {
                        return;
                    }
                }
            }

            if (isRunCurrent(token, runCompletionKey)) {
                setIsVerified(false);
                setCompletionMessage(nextCompletionMessage);
            }
            if (lastError) {
                console.error('Failed to complete video purchase verification');
            }
        } catch {
            if (isRunCurrent(token, runCompletionKey)) {
                setIsVerified(false);
                setCompletionMessage('We could not verify this purchase yet. Please refresh or contact support.');
            }
        } finally {
            if (isRunCurrent(token, runCompletionKey)) {
                isVerifyingRef.current = false;
                setIsPending(false);
            }
        }
    }, [authToken, completionKey, getUserInfo, isRunCurrent, isUserStateLoading, refreshEntitlements, sessionId, videoParam]);

    useEffect(() => {
        isMountedRef.current = true;
        void verifyPurchase();
        return () => {
            isMountedRef.current = false;
        };
    }, [verifyPurchase]);

    const hasCurrentCompletionState = completionStateKey === completionKey;
    const currentIsVerified = hasCurrentCompletionState ? isVerified : false;
    const currentIsPending = hasCurrentCompletionState ? isPending : !!sessionId && !!authToken;
    const currentCompletionMessage = hasCurrentCompletionState
        ? completionMessage
        : sessionId ? null : 'Purchase session is missing. Please contact support if this payment completed.';

    usePageActivationRefresh(!currentIsVerified && !!sessionId && !isUserStateLoading, verifyPurchase);

    return (
        <main className={styles.page}>
            <section className={styles.hero}>
                <div className={styles.heroContent}>
                    <span className={styles.heroTag}>
                        {currentIsVerified ? 'Purchase confirmed' : currentIsPending ? 'Verifying purchase' : 'Purchase pending'}
                    </span>
                    <h1 className={styles.heroTitle}>
                        {currentIsVerified ? 'You are all set' : currentIsPending ? 'Checking your payment' : 'Verification needed'}
                    </h1>
                    <p className={styles.heroSubtitle}>
                        {currentIsVerified
                            ? 'Your purchase was successful. Jump back in now or explore your library whenever you are ready.'
                            : currentIsPending
                                ? 'We are confirming your Stripe payment before unlocking this fight.'
                            : 'Some payment methods finish asynchronously. Use Check again, or contact support if Stripe shows a successful charge.'}
                    </p>
                </div>
            </section>

            <section className={styles.section}>
                <div className={styles.card}>
                    <h2>{currentIsVerified ? 'Ready to watch?' : 'Access is not verified yet'}</h2>
                    <p>
                        {currentIsVerified
                            ? 'Head straight back to your fight, or browse everything you have unlocked.'
                            : 'We will only show the watch action once this purchase is confirmed against Stripe.'}
                    </p>
                    {currentCompletionMessage ? <p>{currentCompletionMessage}</p> : null}
                    <div className={styles.actions}>
                        {!currentIsVerified && sessionId ? (
                            <button
                                type="button"
                                className={styles.secondaryButton}
                                onClick={() => void verifyPurchase()}
                                disabled={currentIsPending}
                            >
                                {currentIsPending ? 'Checking...' : 'Check again'}
                            </button>
                        ) : null}
                        {watchUrl && currentIsVerified ? (
                            <Link to={watchUrl} className={styles.primaryButton}>
                                Watch now
                            </Link>
                        ) : null}
                        <Link to={`${Routes.MY_LIBRARY}?tab=purchases`} className={styles.secondaryButton}>
                            View my library
                        </Link>
                        <Link to={Routes.VIDEOS} className={styles.ghostButton}>
                            Browse videos
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default PurchaseSuccess;
